
import { defineComponent } from 'vue'

import type { ApolloQueryResult } from 'apollo-client'
import type { FetchResult } from 'apollo-link'

import CancelUnifiedSubscriptionMutation from '~/apollo/mutations/owner/cancelUnifiedSubscription.gql'
import ResumeUnifiedSubscriptionMutation from '~/apollo/mutations/owner/resumeUnifiedSubscription.gql'
import CreateBillingPortalSessionMutation from '~/apollo/mutations/owner/createBillingPortalSession.gql'
import DetachSavedCardMutation from '~/apollo/mutations/owner/detachSavedCard.gql'
import SetDefaultCardMutation from '~/apollo/mutations/owner/setDefaultCard.gql'
import UpdateAutoTopupMutation from '~/apollo/mutations/owner/updateAutoTopup.gql'
import UnifiedBillingQuery from '~/apollo/queries/owner/unifiedBilling.gql'
import UnifiedBillingCardsQuery from '~/apollo/queries/owner/unifiedBillingCards.gql'
import UnifiedBillingCreditsQuery from '~/apollo/queries/owner/unifiedBillingCredits.gql'
import {
  BillingInterval,
  UnifiedProductPlan,
  type CreateBillingPortalSessionMutation as CreateBillingPortalSessionMutationData,
  type DetachSavedCardMutation as DetachSavedCardMutationData,
  type SetDefaultCardMutation as SetDefaultCardMutationData,
  type UnifiedBillingQuery as UnifiedBillingQueryData,
  type UpdateAutoTopupMutation as UpdateAutoTopupMutationData
} from '~/types/types'
import { OwnerBillingVersion } from '~/types/types'
import {
  centsToDollars,
  mapUnifiedCardToPaymentMethod,
  mapUnifiedInvoiceToView,
  type InvoiceView,
  type PaymentMethodView
} from '~/utils/billing'
import { formatDate } from '~/utils/date'
import { pollUntil } from '~/utils/polling'
import { formatUSD } from '~/utils/string'

import OwnerDetailMixin from '~/mixins/ownerDetailMixin'

import type { V2PlanConfig } from './v2PlanConfig'
import { ENTERPRISE_PLAN, OPEN_SOURCE_PLAN, TEAM_PLAN } from './v2PlanConfig'

type UnifiedSubscription = NonNullable<
  NonNullable<UnifiedBillingQueryData['owner']>['productSubscription']
>
type UnifiedProductData = Extract<UnifiedSubscription['product'], { __typename?: 'UnifiedProduct' }>

export default defineComponent({
  name: 'BillingV2Page',
  mixins: [OwnerDetailMixin],
  data() {
    return {
      loading: true,
      cardsLoading: false,
      subscription: null as UnifiedSubscription | null,
      showAddCreditModal: false,
      cardToDelete: null as string | null,
      deletingCard: false,
      showCancelModal: false,
      cancellingSubscription: false,
      resumingSubscription: false
    }
  },
  async fetch() {
    // V1 free users see the V2 page for upgrade cards only — skip the unified billing query
    if (this.owner?.billingVersion !== OwnerBillingVersion.V2) {
      this.loading = false
      return
    }

    const { owner, provider } = this.$route.params
    try {
      const refetch = true
      const response: ApolloQueryResult<UnifiedBillingQueryData> = await this.$fetchGraphqlData(
        UnifiedBillingQuery,
        {
          login: owner,
          provider: this.$providerMetaMap[provider].value
        },
        refetch
      )
      this.subscription = response.data?.owner?.productSubscription ?? null
    } catch (error) {
      this.$logErrorAndToast(
        error as Error,
        'We encountered an error while fetching your billing details. Please try again later.'
      )
    } finally {
      this.loading = false
    }
  },
  computed: {
    ownerId(): string {
      return (this as unknown as { owner: { id: string } }).owner?.id ?? ''
    },
    product(): UnifiedProductData | null {
      const product = this.subscription?.product
      if (product?.__typename === 'UnifiedProduct') return product
      return null
    },
    billingDetails() {
      return this.subscription?.billingDetails ?? null
    },
    planType(): 'enterprise' | 'team' | 'free' {
      if (!this.product) return 'free'
      if (this.product.plan === UnifiedProductPlan.PlanEnterprise) return 'enterprise'
      if (
        this.product.plan === UnifiedProductPlan.PlanTeam ||
        this.product.plan === UnifiedProductPlan.PlanTrial
      ) {
        return 'team'
      }
      return 'free'
    },
    isEnterprise(): boolean {
      return this.planType === 'enterprise'
    },
    isActivePlan(): boolean {
      return this.planType === 'team'
    },
    isV1FreePlan(): boolean {
      return this.owner?.billingVersion !== OwnerBillingVersion.V2
    },
    isTrial(): boolean {
      return this.product?.plan === UnifiedProductPlan.PlanTrial
    },
    cancelAtPeriodEnd(): boolean {
      return this.billingDetails?.cancelAtPeriodEnd ?? false
    },
    formattedBillingInterval(): string {
      if (!this.billingDetails?.billingInterval) return ''
      return this.billingDetails.billingInterval === BillingInterval.Monthly ? 'Monthly' : 'Yearly'
    },
    formattedNextRenewal(): string {
      if (!this.billingDetails?.nextBillingCycle) return ''
      return formatDate(this.billingDetails.nextBillingCycle)
    },
    enterpriseActivePlan() {
      return {
        name: 'Enterprise plan',
        price: 'Custom pricing',
        contributors: this.product?.seatQuantity ?? 0,
        ossTargets: this.product?.scaTargetQuantity ?? 0,
        aiCreditBalance: centsToDollars(this.billingDetails?.usageCredits ?? 0),
        nextRenewal: this.formattedNextRenewal,
        billingInterval: this.formattedBillingInterval || 'Yearly'
      }
    },
    activePlan() {
      return {
        name: 'Team plan',
        price:
          (this.billingDetails?.billingInterval === BillingInterval.Annual
            ? TEAM_PLAN.annualPrice
            : TEAM_PLAN.price) ?? '',
        contributors: this.product?.seatQuantity ?? 0,
        ossTargets: this.product?.scaTargetQuantity ?? 0,
        aiCreditBalance: centsToDollars(this.billingDetails?.usageCredits ?? 0),
        autoRechargeEnabled: this.product?.isAutoTopupEnabled ?? false,
        autoTopupLimitExceeded: this.product?.isAutoTopupCycleLimitExceeded ?? false,
        balanceThreshold: this.product?.autoTopupThreshold ?? 0,
        autoRechargeAmount: this.product?.autoTopupAmount ?? 0,
        maxRechargePerMonth: this.product?.autoTopupMax ?? 0,
        nextRenewal: this.formattedNextRenewal,
        billingInterval: this.isTrial ? '' : this.formattedBillingInterval || 'Monthly',
        promotionalCredits: centsToDollars(this.billingDetails?.promotionalCredits ?? 0),
        currentInvoiceAmount: centsToDollars(this.billingDetails?.upcomingBillingAmount ?? 0)
      }
    },
    autoTopupMaxDisplay(): string {
      // formatUSD already prepends the currency symbol — no extra `$`.
      return formatUSD(centsToDollars(this.product?.autoTopupMax ?? 0))
    },
    paymentMethods(): PaymentMethodView[] {
      const cards = this.billingDetails?.cards
      if (!cards?.length) return []
      return cards
        .filter((c): c is NonNullable<typeof c> => Boolean(c?.endingIn))
        .map(mapUnifiedCardToPaymentMethod)
    },
    recentInvoices(): InvoiceView[] {
      const invoices = this.billingDetails?.invoices
      if (!invoices?.length) return []
      return invoices
        .filter((inv): inv is NonNullable<typeof inv> => Boolean(inv))
        .map(mapUnifiedInvoiceToView)
    },
    currentPlan(): V2PlanConfig {
      return OPEN_SOURCE_PLAN
    },
    teamPlanConfig(): V2PlanConfig {
      return TEAM_PLAN
    },
    cardToDeleteDetails(): {
      brand: string
      brandLabel: string
      lastFour: string
      expiryLabel: string
    } | null {
      if (!this.cardToDelete) return null
      return this.paymentMethods.find((m) => m.id === this.cardToDelete) ?? null
    },
    enterprisePlanConfig(): V2PlanConfig {
      return ENTERPRISE_PLAN
    }
  },
  methods: {
    async refetchCards(): Promise<void> {
      this.cardsLoading = true
      try {
        const { owner, provider } = this.$route.params
        const response: ApolloQueryResult<UnifiedBillingQueryData> = await this.$fetchGraphqlData(
          UnifiedBillingCardsQuery,
          { login: owner, provider: this.$providerMetaMap[provider].value },
          true
        )
        const cards = response.data?.owner?.productSubscription?.billingDetails?.cards
        if (this.subscription?.billingDetails && cards) {
          this.subscription.billingDetails.cards = cards
        }
      } catch (error) {
        this.$logErrorAndToast(
          error as Error,
          'Failed to refresh payment methods. Please try again later.'
        )
      } finally {
        this.cardsLoading = false
      }
    },
    async refetchCredits(): Promise<void> {
      try {
        const { owner, provider } = this.$route.params
        const response: ApolloQueryResult<UnifiedBillingQueryData> = await this.$fetchGraphqlData(
          UnifiedBillingCreditsQuery,
          { login: owner, provider: this.$providerMetaMap[provider].value },
          true
        )
        const billingDetails = response.data?.owner?.productSubscription?.billingDetails
        if (this.subscription?.billingDetails && billingDetails) {
          this.subscription.billingDetails.usageCredits = billingDetails.usageCredits
          this.subscription.billingDetails.promotionalCredits = billingDetails.promotionalCredits
        }
      } catch (error) {
        this.$logErrorAndToast(
          error as Error,
          'Failed to refresh credit balance. Please try again later.'
        )
      }
    },
    navigateToCheckout(): void {
      const route = this.$generateRoute(['settings', 'billing', 'checkout'], false, true)
      this.$router.push(`${route}?plan=PLAN_TEAM&interval=monthly`)
    },
    async updateAutoTopup(settings: {
      isAutoTopupEnabled?: boolean
      autoTopupThreshold?: number
      autoTopupAmount?: number
      autoTopupMax?: number
    }) {
      try {
        ;(await this.$applyGraphqlMutation(UpdateAutoTopupMutation, {
          ownerId: this.owner?.id,
          ...settings
        })) as FetchResult<UpdateAutoTopupMutationData>
        await this.$fetch()
      } catch (error) {
        this.$logErrorAndToast(
          error as Error,
          'We encountered an error while updating auto top-up settings. Please try again later.'
        )
      }
    },
    async detachSavedCard() {
      if (!this.cardToDelete) return
      this.deletingCard = true
      try {
        ;(await this.$applyGraphqlMutation(DetachSavedCardMutation, {
          ownerId: this.owner?.id,
          paymentMethodId: this.cardToDelete
        })) as FetchResult<DetachSavedCardMutationData>
        this.cardToDelete = null
        this.$toast.success('Card removed successfully.')
        await this.refetchCards()
      } catch (error) {
        this.$logErrorAndToast(
          error as Error,
          'We encountered an error while removing the card. Please try again later.'
        )
      } finally {
        this.deletingCard = false
      }
    },
    async openBillingPortal() {
      try {
        const result = (await this.$applyGraphqlMutation(CreateBillingPortalSessionMutation, {
          ownerId: this.owner?.id
        })) as FetchResult<CreateBillingPortalSessionMutationData>
        const url = result.data?.createBillingPortalSession?.url
        if (url) {
          window.location.href = url
        }
      } catch (error) {
        this.$logErrorAndToast(
          error as Error,
          'We encountered an error while opening the billing portal. Please try again later.'
        )
      }
    },
    async onCreditAdded() {
      const previousCredits = this.subscription?.billingDetails?.usageCredits ?? 0

      const confirmed = await pollUntil(async () => {
        try {
          await this.refetchCredits()
          const currentCredits = this.subscription?.billingDetails?.usageCredits ?? 0
          return currentCredits !== previousCredits
        } catch {
          return false
        }
      })

      this.showAddCreditModal = false
      if (confirmed) {
        this.$toast.success('Credit added successfully.')
      } else {
        this.$toast.success('Credit added! Balance may take a moment to update.')
      }
    },
    async resumeSubscription() {
      this.resumingSubscription = true
      try {
        const result = (await this.$applyGraphqlMutation(ResumeUnifiedSubscriptionMutation, {
          ownerId: this.owner?.id
        })) as FetchResult<{ resumeUnifiedSubscription?: { ok?: boolean; error?: string } }>
        const payload = result.data?.resumeUnifiedSubscription
        if (payload?.error) {
          this.$toast.danger(payload.error)
        } else {
          this.$toast.success('Subscription resumed successfully.')
          await this.$fetch()
        }
      } catch (error) {
        this.$logErrorAndToast(
          error as Error,
          'We encountered an error while resuming your subscription. Please try again later.'
        )
      } finally {
        this.resumingSubscription = false
      }
    },
    async cancelSubscription() {
      this.cancellingSubscription = true
      try {
        const result = (await this.$applyGraphqlMutation(CancelUnifiedSubscriptionMutation, {
          ownerId: this.owner?.id
        })) as FetchResult<{ cancelUnifiedSubscription?: { ok?: boolean; error?: string } }>
        const payload = result.data?.cancelUnifiedSubscription
        if (payload?.error) {
          this.$toast.danger(payload.error)
        } else {
          this.$toast.success('Subscription cancelled successfully.')
          await this.$fetch()
        }
      } catch (error) {
        this.$logErrorAndToast(
          error as Error,
          'We encountered an error while cancelling your subscription. Please try again later.'
        )
      } finally {
        this.cancellingSubscription = false
        this.showCancelModal = false
      }
    },
    async setDefaultCard(paymentMethodId: string) {
      try {
        ;(await this.$applyGraphqlMutation(SetDefaultCardMutation, {
          ownerId: this.owner?.id,
          paymentMethodId
        })) as FetchResult<SetDefaultCardMutationData>
        this.$toast.success('Default card updated.')
        await this.refetchCards()
      } catch (error) {
        this.$logErrorAndToast(
          error as Error,
          'We encountered an error while updating the default card. Please try again later.'
        )
      }
    }
  }
})
